import React from 'react';

import AvomaDropdown from 'components/Common/AvomaDropdown';
import AvomaIcon from 'components/Common/AvomaIcon';

import { FORMAT_OPTIONS } from 'constants/smartCategories';

import { ReactComponent as CalendarIcon } from 'images/ic_calendar.svg';
import { ReactComponent as NumberIcon } from 'images/ic_number.svg';
import { ReactComponent as TextIcon } from 'images/ic_text.svg';

import Tokens from 'styles/tokens';

interface IFormatOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  lengthOptions?: Array<{
    label: string;
    value: string;
    disabled?: boolean;
  }>;
}

interface ISmartTopicFormattingProps {
  selectedDataTypeOption: string;
  selectedFormatOption: string;
  selectedLengthOption: string;
  isAdminOrTopicOwner: boolean;
  onDataTypeChange: (value: string) => void;
  onFormatChange: (
    value: string,
    defaultLengthOption: string | undefined
  ) => void;
  onLengthChange: (value: string) => void;
}

const dataTypeOptions = [
  {
    label: (
      <div className='flex items-center'>
        <AvomaIcon className='mr-3' RenderIcon={TextIcon} small />
        <span>Text</span>
      </div>
    ),
    value: 'text'
  },
  {
    label: (
      <div className='flex items-center'>
        <AvomaIcon className='mr-3' RenderIcon={NumberIcon} small />
        <span>Number</span>
      </div>
    ),
    value: 'number'
  },
  {
    label: (
      <div className='flex items-center'>
        <AvomaIcon className='mr-3' RenderIcon={CalendarIcon} small />
        <span>Date/Time</span>
      </div>
    ),
    value: 'date_and_time'
  }
];

const SmartTopicFormatting: React.FC<ISmartTopicFormattingProps> = ({
  selectedDataTypeOption,
  selectedFormatOption,
  selectedLengthOption,
  isAdminOrTopicOwner,
  onDataTypeChange,
  onFormatChange,
  onLengthChange
}) => {
  const selectedLengthOptions = (
    FORMAT_OPTIONS as Record<string, IFormatOption[]>
  )[selectedDataTypeOption]?.find(
    option => option.value === selectedFormatOption
  )?.lengthOptions;

  const handleFormatChange = (val: string) => {
    const defaultLengthOption = (
      FORMAT_OPTIONS as Record<string, IFormatOption[]>
    )[selectedDataTypeOption].find(option => option.value === val)
      ?.lengthOptions?.[0]?.value;

    onFormatChange(val, defaultLengthOption);
  };

  const dropdownStyles = {
    width: '200px',
    ...Tokens.type.body,
    pointerEvents: !isAdminOrTopicOwner ? 'none' : 'auto',
    opacity: !isAdminOrTopicOwner ? 0.4 : 1
  };

  return (
    <div className='flex flex-wrap items-center gap-4'>
      <div>
        <p className='text-sm font-bold mb-1'>Data Type</p>
        <AvomaDropdown
          items={dataTypeOptions.map(option => ({
            ...option,
            disabled: !isAdminOrTopicOwner
          }))}
          value={selectedDataTypeOption}
          onSelect={onDataTypeChange}
          containerStyles={dropdownStyles}
        />
      </div>

      {selectedDataTypeOption && (
        <div>
          <p className='text-sm font-bold mb-1'>Format</p>
          <AvomaDropdown
            items={(FORMAT_OPTIONS as Record<string, IFormatOption[]>)[
              selectedDataTypeOption
            ].map(option => ({
              ...option,
              disabled: !isAdminOrTopicOwner
            }))}
            value={selectedFormatOption}
            onSelect={handleFormatChange}
            containerStyles={dropdownStyles}
          />
        </div>
      )}

      {selectedLengthOptions && (
        <div>
          <p className='text-sm font-bold mb-1'>Length</p>
          <AvomaDropdown
            items={selectedLengthOptions.map(option => ({
              ...option,
              disabled: !isAdminOrTopicOwner
            }))}
            value={selectedLengthOption}
            onSelect={onLengthChange}
            containerStyles={dropdownStyles}
          />
        </div>
      )}
    </div>
  );
};

export default SmartTopicFormatting;
